/**
 * Guardrail Scanners
 * 
 * Scan the codebase to build the Truthpack - ground truth data
 * that AI agents use to verify their claims.
 */

import { glob } from 'fast-glob';
import * as fs from 'fs/promises';
import * as path from 'path';
import type { 
  RouteInfo, 
  EnvVarInfo, 
  AuthRule, 
  ContractInfo,
  TruthpackData,
  TruthpackMeta,
  ScannerConfig 
} from '../types.js';

export class TruthpackScanner {
  private projectRoot: string;
  private config: ScannerConfig;

  constructor(projectRoot: string, config: ScannerConfig) {
    this.projectRoot = projectRoot;
    this.config = config;
  }

  async scan(): Promise<TruthpackData> {
    const [routes, envVars, authRules, contracts] = await Promise.all([
      this.config.routes.enabled ? this.scanRoutes() : [],
      this.config.env.enabled ? this.scanEnvVars() : [],
      this.config.auth.enabled ? this.scanAuth() : [],
      this.config.contracts.enabled ? this.scanContracts() : [],
    ]);

    const meta: TruthpackMeta = {
      version: '1.0.0',
      generatedAt: new Date().toISOString(),
      hash: await this.computeHash({ routes, envVars, authRules, contracts }),
      scannerVersions: {
        routes: '1.0.0',
        env: '1.0.0',
        auth: '1.0.0',
        contracts: '1.0.0',
      },
      summary: {
        routes: routes.length,
        envVars: envVars.length,
        authRules: authRules.length,
        contracts: contracts.length,
      },
    };

    return { routes, envVars, authRules, contracts, meta };
  }

  private async scanRoutes(): Promise<RouteInfo[]> {
    const routes: RouteInfo[] = [];
    const files = await glob(this.config.routes.patterns, {
      cwd: this.projectRoot,
      ignore: ['**/node_modules/**', '**/dist/**', '**/build/**'],
      absolute: true,
    });

    for (const file of files) {
      const content = await fs.readFile(file, 'utf-8');
      const fileRoutes = this.extractRoutes(content, file);
      routes.push(...fileRoutes);
    }

    return routes;
  }

  private extractRoutes(content: string, file: string): RouteInfo[] {
    const routes: RouteInfo[] = [];
    const relativePath = path.relative(this.projectRoot, file);

    // Express-style routes: app.get('/path', handler)
    const expressPattern = /\.(get|post|put|patch|delete|options|head)\s*\(\s*['"`]([^'"`]+)['"`]/gi;
    let match;
    
    while ((match = expressPattern.exec(content)) !== null) {
      const method = match[1].toUpperCase() as RouteInfo['method'];
      const routePath = match[2];
      const line = content.substring(0, match.index).split('\n').length;
      
      routes.push({
        path: routePath,
        method,
        handler: this.extractHandler(content, match.index),
        file: relativePath,
        line,
        parameters: this.extractRouteParams(routePath),
        middleware: this.extractMiddleware(content, match.index),
        auth: this.detectAuthRequirement(content, match.index),
      });
    }

    // Router-style routes: router.get('/path', handler)
    const routerPattern = /router\.(get|post|put|patch|delete|options|head)\s*\(\s*['"`]([^'"`]+)['"`]/gi;
    
    while ((match = routerPattern.exec(content)) !== null) {
      const method = match[1].toUpperCase() as RouteInfo['method'];
      const routePath = match[2];
      const line = content.substring(0, match.index).split('\n').length;
      
      routes.push({
        path: routePath,
        method,
        handler: this.extractHandler(content, match.index),
        file: relativePath,
        line,
        parameters: this.extractRouteParams(routePath),
        middleware: this.extractMiddleware(content, match.index),
        auth: this.detectAuthRequirement(content, match.index),
      });
    }

    // Next.js API routes: export default function handler
    if (file.includes('/api/') || file.includes('\\api\\')) {
      const nextPattern = /export\s+(default\s+)?(async\s+)?function\s+(\w+)/i;
      const nextMatch = content.match(nextPattern);
      
      if (nextMatch) {
        const apiPath = this.filePathToApiRoute(file);
        routes.push({
          path: apiPath,
          method: 'GET', // Next.js handles all methods
          handler: nextMatch[3] || 'handler',
          file: relativePath,
          line: 1,
          parameters: this.extractRouteParams(apiPath),
          middleware: [],
        });
      }
    }

    return routes;
  }

  private extractHandler(content: string, index: number): string {
    const afterRoute = content.substring(index, index + 200);
    const handlerMatch = afterRoute.match(/,\s*(\w+)/);
    return handlerMatch?.[1] || 'anonymous';
  }

  private extractRouteParams(routePath: string): RouteInfo['parameters'] {
    const params: RouteInfo['parameters'] = [];
    const paramPattern = /:(\w+)/g;
    let match;
    
    while ((match = paramPattern.exec(routePath)) !== null) {
      params.push({
        name: match[1],
        type: 'string',
        required: true,
        in: 'path',
      });
    }
    
    return params;
  }

  private extractMiddleware(content: string, index: number): string[] {
    const middleware: string[] = [];
    const beforeRoute = content.substring(Math.max(0, index - 500), index);
    
    // Look for common middleware patterns
    const authMiddleware = ['authenticate', 'auth', 'requireAuth', 'isAuthenticated', 'protect'];
    const validationMiddleware = ['validate', 'validateBody', 'validateParams'];
    
    for (const mw of [...authMiddleware, ...validationMiddleware]) {
      if (beforeRoute.includes(mw)) {
        middleware.push(mw);
      }
    }
    
    return middleware;
  }

  private detectAuthRequirement(content: string, index: number): RouteInfo['auth'] | undefined {
    const context = content.substring(Math.max(0, index - 200), index + 200);
    const authPatterns = ['authenticate', 'auth', 'requireAuth', 'isAuthenticated', 'protect', 'jwt', 'session'];
    
    for (const pattern of authPatterns) {
      if (context.toLowerCase().includes(pattern)) {
        return { required: true };
      }
    }
    
    return undefined;
  }

  private filePathToApiRoute(file: string): string {
    const relativePath = path.relative(this.projectRoot, file);
    const apiMatch = relativePath.match(/(?:pages|app)\/api\/(.+)\.(ts|js|tsx|jsx)/i);
    
    if (apiMatch) {
      let route = '/' + apiMatch[1]
        .replace(/\[\.\.\.(\w+)\]/g, '*') // Catch-all
        .replace(/\[(\w+)\]/g, ':$1')     // Dynamic segments
        .replace(/\/index$/, '')          // Remove index suffix
        .replace(/\\/g, '/');             // Normalize path
      return '/api' + route;
    }
    
    return '/api/unknown';
  }

  private async scanEnvVars(): Promise<EnvVarInfo[]> {
    const envVars: EnvVarInfo[] = [];
    
    for (const envFile of this.config.env.files) {
      const filePath = path.join(this.projectRoot, envFile);
      
      try {
        const content = await fs.readFile(filePath, 'utf-8');
        const lines = content.split('\n');
        
        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          
          // Skip comments and empty lines
          if (line.startsWith('#') || !line) continue;
          
          const match = line.match(/^([A-Z_][A-Z0-9_]*)=(.*)$/);
          if (match) {
            const [, name, value] = match;
            const isSensitive = this.isSensitiveEnvVar(name);
            
            envVars.push({
              name,
              file: envFile,
              line: i + 1,
              hasDefault: value.length > 0,
              defaultValue: isSensitive ? '[REDACTED]' : value,
              required: !value.length,
              sensitive: isSensitive,
            });
          }
        }
      } catch {
        // File doesn't exist, skip
      }
    }
    
    return envVars;
  }

  private isSensitiveEnvVar(name: string): boolean {
    const sensitivePatterns = [
      'SECRET', 'KEY', 'PASSWORD', 'TOKEN', 'PRIVATE',
      'CREDENTIAL', 'AUTH', 'API_KEY', 'APIKEY'
    ];
    
    return sensitivePatterns.some(pattern => 
      name.toUpperCase().includes(pattern)
    );
  }

  private async scanAuth(): Promise<AuthRule[]> {
    const authRules: AuthRule[] = [];
    const files = await glob(this.config.auth.patterns, {
      cwd: this.projectRoot,
      ignore: ['**/node_modules/**', '**/dist/**', '**/build/**'],
      absolute: true,
    });

    for (const file of files) {
      const content = await fs.readFile(file, 'utf-8');
      const relativePath = path.relative(this.projectRoot, file);
      
      // Detect roles
      const rolePattern = /(?:role|ROLE|Role)\s*[=:]\s*['"`](\w+)['"`]/g;
      let match;
      
      while ((match = rolePattern.exec(content)) !== null) {
        const line = content.substring(0, match.index).split('\n').length;
        authRules.push({
          name: match[1],
          type: 'role',
          file: relativePath,
          line,
        });
      }

      // Detect permissions
      const permPattern = /(?:permission|PERMISSION|Permission)\s*[=:]\s*['"`](\w+)['"`]/g;
      
      while ((match = permPattern.exec(content)) !== null) {
        const line = content.substring(0, match.index).split('\n').length;
        authRules.push({
          name: match[1],
          type: 'permission',
          file: relativePath,
          line,
        });
      }

      // Detect middleware
      const middlewarePattern = /(?:export\s+(?:const|function|async function)\s+)?(authenticate|requireAuth|isAuthenticated|protect|authMiddleware)\b/gi;
      
      while ((match = middlewarePattern.exec(content)) !== null) {
        const line = content.substring(0, match.index).split('\n').length;
        authRules.push({
          name: match[1],
          type: 'middleware',
          file: relativePath,
          line,
        });
      }
    }

    // Deduplicate
    return Array.from(new Map(authRules.map(r => [`${r.name}-${r.type}`, r])).values());
  }

  private async scanContracts(): Promise<ContractInfo[]> {
    const contracts: ContractInfo[] = [];
    const files = await glob(['**/*.ts', '**/*.tsx'], {
      cwd: this.projectRoot,
      ignore: ['**/node_modules/**', '**/dist/**', '**/build/**', '**/*.test.ts', '**/*.spec.ts'],
      absolute: true,
    });

    for (const file of files) {
      const content = await fs.readFile(file, 'utf-8');
      const relativePath = path.relative(this.projectRoot, file);
      
      // TypeScript interfaces
      const interfacePattern = /export\s+interface\s+(\w+)/g;
      let match;
      
      while ((match = interfacePattern.exec(content)) !== null) {
        const line = content.substring(0, match.index).split('\n').length;
        contracts.push({
          name: match[1],
          type: 'interface',
          file: relativePath,
          line,
          source: 'typescript',
        });
      }

      // TypeScript types
      const typePattern = /export\s+type\s+(\w+)\s*=/g;
      
      while ((match = typePattern.exec(content)) !== null) {
        const line = content.substring(0, match.index).split('\n').length;
        contracts.push({
          name: match[1],
          type: 'type',
          file: relativePath,
          line,
          source: 'typescript',
        });
      }

      // Zod schemas
      const zodPattern = /(?:export\s+)?(?:const|let)\s+(\w+Schema|\w+Validator)\s*=\s*z\./g;
      
      while ((match = zodPattern.exec(content)) !== null) {
        const line = content.substring(0, match.index).split('\n').length;
        contracts.push({
          name: match[1],
          type: 'schema',
          file: relativePath,
          line,
          source: 'zod',
        });
      }

      // Enums
      const enumPattern = /export\s+(?:const\s+)?enum\s+(\w+)/g;
      
      while ((match = enumPattern.exec(content)) !== null) {
        const line = content.substring(0, match.index).split('\n').length;
        contracts.push({
          name: match[1],
          type: 'enum',
          file: relativePath,
          line,
          source: 'typescript',
        });
      }
    }

    return contracts;
  }

  private async computeHash(data: Omit<TruthpackData, 'meta'>): Promise<string> {
    const str = JSON.stringify(data);
    // Simple hash for now - in production use xxhash
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(16).padStart(16, '0');
  }
}

export async function scanTruthpack(projectRoot: string, config: ScannerConfig): Promise<TruthpackData> {
  const scanner = new TruthpackScanner(projectRoot, config);
  return scanner.scan();
}

export async function saveTruthpack(projectRoot: string, data: TruthpackData): Promise<void> {
  const truthpackDir = path.join(projectRoot, '.vibecheck', 'truthpack');
  
  await fs.mkdir(truthpackDir, { recursive: true });
  
  await Promise.all([
    fs.writeFile(
      path.join(truthpackDir, 'routes.json'),
      JSON.stringify({ version: '1.0.0', generatedAt: data.meta.generatedAt, routes: data.routes }, null, 2)
    ),
    fs.writeFile(
      path.join(truthpackDir, 'env.json'),
      JSON.stringify({ version: '1.0.0', generatedAt: data.meta.generatedAt, variables: data.envVars }, null, 2)
    ),
    fs.writeFile(
      path.join(truthpackDir, 'auth.json'),
      JSON.stringify({ version: '1.0.0', generatedAt: data.meta.generatedAt, rules: data.authRules }, null, 2)
    ),
    fs.writeFile(
      path.join(truthpackDir, 'contracts.json'),
      JSON.stringify({ version: '1.0.0', generatedAt: data.meta.generatedAt, contracts: data.contracts }, null, 2)
    ),
    fs.writeFile(
      path.join(truthpackDir, 'meta.json'),
      JSON.stringify(data.meta, null, 2)
    ),
  ]);
}

export async function loadTruthpack(projectRoot: string): Promise<TruthpackData | null> {
  const truthpackDir = path.join(projectRoot, '.vibecheck', 'truthpack');
  
  try {
    const [routesFile, envFile, authFile, contractsFile, metaFile] = await Promise.all([
      fs.readFile(path.join(truthpackDir, 'routes.json'), 'utf-8'),
      fs.readFile(path.join(truthpackDir, 'env.json'), 'utf-8'),
      fs.readFile(path.join(truthpackDir, 'auth.json'), 'utf-8'),
      fs.readFile(path.join(truthpackDir, 'contracts.json'), 'utf-8'),
      fs.readFile(path.join(truthpackDir, 'meta.json'), 'utf-8'),
    ]);

    const routes = JSON.parse(routesFile).routes || [];
    const envVars = JSON.parse(envFile).variables || [];
    const authRules = JSON.parse(authFile).rules || [];
    const contracts = JSON.parse(contractsFile).contracts || [];
    const meta = JSON.parse(metaFile);

    return { routes, envVars, authRules, contracts, meta };
  } catch {
    return null;
  }
}
